import React, { useState, useEffect, useRef } from"react";
import { useLocation, useNavigate } from"react-router-dom";
import useSkillGenieSession from"../hooks/useSkillGenieSession";
import API_BASE_URL from"../Config/config";
import FeatureGuard from"./access/FeatureGuard";
import TutorShell, { RailSection, RailStat, RailTip } from "./aiTutor/TutorShell";
import { useTutorLanguage, LanguagePicker } from "./aiTutor/languages";
import {
  TutorMessages,
  TutorComposer,
  TutorBadge,
  TutorEmptyState,
} from "./aiTutor/TutorChat";
import NexusUsageBadge from "../NextGenBuilder/components/NexusUsageBadge";
import useNexusLearnerBilling from "../NextGenBuilder/hooks/useNexusLearnerBilling";

// Optional FE map for nicer display (or import from shared utils if exposed)

const ResumeReviewPage = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";

 if (isTenantMode) {
   return <ResumeReviewPageContent mode={mode} />;
 }

 return (
   <FeatureGuard feature="TSG_AI">
     <ResumeReviewPageContent mode={mode} />
   </FeatureGuard>
 );
};

const ResumeReviewPageContent = ({ mode = "learner" }) => {
 const [input, setInput] = useState("");
 const [chat, setChat] = useState([]);
 const [file, setFile] = useState(null);
 const [loading, setLoading] = useState(false);
 const chatEndRef = useRef(null);
 const navigate = useNavigate();

 const isTenantMode = mode === "tenant";

 // 🔹 language from navigation state OR saved session
 const location = useLocation();
 const { session } = useSkillGenieSession();
  const { langCode, langName, setLanguage } = useTutorLanguage({
    initialLanguage: location.state?.language || session?.language,
  });

 // TSG Nexus feature billing — inert outside /nextgen-builder/*.
 const nexus = useNexusLearnerBilling("AI_TUTOR", { subFeature: "RESUME_REVIEW" });

 const handleUpload = (e) => setFile(e.target.files[0] || null);

 const handleAsk = async () => {
 if (!input.trim() && !file) return;

 const user = JSON.parse(localStorage.getItem("user") ||"{}");
 // NextGen Builder accounts don't have `.userId`/`.user_id` — fall back to
 // the namespaced pseudo-identity written to localStorage by
 // NextGenAuthContext.jsx, same as ExplainPage.jsx already does.
 const userId = user?.userId || user?.user_id || localStorage.getItem("user_id") ||"UnknownUser";

 if (!userId || userId ==="UnknownUser") {
 alert("Please log in to use this feature.");
 navigate(isTenantMode ? "/nextgen-builder/login" : "/login");
 return;
 }

    const currentInput = input;
    const currentFile = file;
    setChat((prev) => [...prev, { role: "user", content: currentInput || (currentFile ? "📄 Uploaded resume" : "") }]);
    setInput("");
    setFile(null);
    setLoading(true);

    const formData = new FormData();
    formData.append("prompt", currentInput);
    formData.append("userId", userId);
    formData.append("language", langCode);
    formData.append("chatHistory", JSON.stringify(chat));
    if (currentFile) formData.append("resume", currentFile);

    try {
      const res = await fetch(`${API_BASE_URL}/tsg/resume_review`, {
        method: "POST",
        // Multipart body unchanged — never set Content-Type by hand here or
        // the browser can't append the boundary. Only identity headers added.
        headers: { ...nexus.headers() },
        body: formData,
      });

      const data = await res.json();

      if (res.status === 402 && nexus.handleQuota(data)) {
        setChat((prev) => [
          ...prev,
          { role: "assistant", content: "You've used all your free Resume Review prompts for this month. Recharge to continue." },
        ]);
        return;
      }

      nexus.noteUsage(res);

      const reply = data.reply || "No reply.";
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: reply },
      ]);
    } catch {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"❌ Error reaching TSG." },
 ]);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  return (
    <TutorShell
      language={<LanguagePicker value={langCode} onChange={setLanguage} />}
      icon={<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>}
      title="Resume Review"
      subtitle="Upload your resume and get specific, section-by-section feedback before you apply."
      onBack={() => navigate(-1)}
      headerExtra={nexus.isNexus ? <NexusUsageBadge feature="AI_TUTOR" subFeature="RESUME_REVIEW" /> : null}
      badges={<TutorBadge label="Lang" value={langName} dot="#22C55E" />}
      rail={
        <>
          <RailSection title="Your document">
            <RailStat
              label="Attached"
              value={file ? file.name : "No file attached"}
              hint={file ? "Sent with your next message." : "PDF, DOC, DOCX or TXT."}
            />
            <RailStat label="Feedback language" value={langName} />
          </RailSection>
          <RailSection title="Ask about">
            {[
              "Is my summary section strong enough?",
              "Rewrite my bullet points to show impact",
              "Does this match a backend developer role?",
              "What is missing for a first job application?",
            ].map((s) => (
              <button
                key={s}
                onClick={() => setInput(s)}
                className="w-full text-left rounded-2xl border border-[#E9E7F3] bg-white px-4 py-3 mb-2 last:mb-0 hover:border-[#5B21B6] hover:bg-[#F5F2FF] transition-all"
              >
                <span className="text-[12px] font-semibold text-[#4B3F72] leading-snug">{s}</span>
              </button>
            ))}
          </RailSection>
          <RailSection title="Tips">
            <RailTip>Attach the file and ask a question together — feedback aimed at a real target role is far more concrete.</RailTip>
          </RailSection>
        </>
      }
      composer={
        <TutorComposer
          value={input}
          onChange={setInput}
          onSubmit={handleAsk}
          loading={loading}
          disabled={!input.trim() && !file}
          placeholder="Ask about your resume or improvements..."
          hint={[nexus.hint, file ? "Attached: " + file.name : null].filter(Boolean).join(" · ") || null}
          leading={
            <label
              title={file ? file.name : "Attach Resume (PDF/DOC)"}
              className={"shrink-0 w-11 h-11 rounded-2xl border flex items-center justify-center cursor-pointer transition-all " + (file ? "border-[#5B21B6] bg-[#F5F2FF]" : "border-[#E9E7F3] bg-white hover:border-[#5B21B6] hover:bg-[#F5F2FF]")}
            >
              <input type="file" accept=".pdf,.doc,.docx,.txt" onChange={handleUpload} className="hidden" />
              <svg className="w-5 h-5 text-[#5B21B6]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </label>
          }
        />
      }
    >
      <TutorMessages
        chat={chat}
        chatEndRef={chatEndRef}
        empty={
          <TutorEmptyState
            icon={<svg className="w-9 h-9 text-[#5B21B6]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>}
            title="Attach your resume to begin"
            description="Use the clip in the message box to attach a PDF or Word document, then ask what you want reviewed."
          />
        }
      />
    </TutorShell>
  );
};

export default ResumeReviewPage;



